import React, { createContext, useContext, useEffect, useState } from 'react';

// Theme context
const ThemeContext = createContext();

// Theme modes
export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

// Theme provider component
export const ThemeProvider = ({ children }) => {
  // Always use light mode - dark mode disabled for now
  const [themeMode, setThemeMode] = useState(THEME_MODES.LIGHT);
  const [actualTheme, setActualTheme] = useState('light');

  // Function to get system theme preference
  const getSystemTheme = () => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches 
      ? THEME_MODES.DARK 
      : THEME_MODES.LIGHT;
  };

  // Function to apply theme to document
  const applyTheme = (theme) => {
    const root = document.documentElement;
    
    if (theme === THEME_MODES.DARK) {
      root.classList.add('dark');
      root.classList.remove('light');
      setActualTheme('dark');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
      setActualTheme('light');
    }
  };

  // Function to update theme mode
  const updateThemeMode = (mode) => {
    console.log(`🎨 Theme mode changing from "${themeMode}" to "${mode}"`);
    setThemeMode(mode);
    localStorage.setItem('theme-mode', mode);
    
    let themeToApply;
    
    if (mode === THEME_MODES.SYSTEM) {
      themeToApply = getSystemTheme();
    } else {
      themeToApply = mode;
    }
    
    console.log(`🎨 Applying theme: "${themeToApply}"`);
    applyTheme(themeToApply);
  };

  // Effect to handle initial theme setup - always light mode
  useEffect(() => {
    console.log(`🎨 Applying light theme (dark mode disabled)`);
    applyTheme(THEME_MODES.LIGHT);
  }, []);

  const value = {
    themeMode,
    actualTheme,
    updateThemeMode,
    isSystemTheme: themeMode === THEME_MODES.SYSTEM,
    isDarkMode: actualTheme === THEME_MODES.DARK,
    isLightMode: actualTheme === THEME_MODES.LIGHT,
    availableModes: THEME_MODES
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

// Theme selector component
export const ThemeSelector = ({ className = "" }) => {
  const { themeMode, updateThemeMode, availableModes } = useTheme();

  const themeOptions = [
    {
      mode: availableModes.LIGHT,
      label: 'Light',
      icon: '☀️',
      description: 'Light theme'
    },
    {
      mode: availableModes.DARK,
      label: 'Dark',
      icon: '🌙',
      description: 'Dark theme'
    },
    {
      mode: availableModes.SYSTEM,
      label: 'System',
      icon: '💻',
      description: 'Match system preference'
    }
  ];

  return (
    <div className={`theme-selector ${className}`}>
      <div className="space-y-2">
        {themeOptions.map((option) => (
          <button
            key={option.mode}
            onClick={() => updateThemeMode(option.mode)}
            className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors text-left ${
              themeMode === option.mode
                ? 'bg-purple-100 dark:bg-purple-900 text-purple-900 dark:text-purple-100 border-2 border-purple-500'
                : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-transparent'
            }`}
          >
            <span className="text-xl" role="img" aria-label={option.label}>
              {option.icon}
            </span>
            <div className="flex-1">
              <div className="font-medium">{option.label}</div>
              <div className="text-sm opacity-70">{option.description}</div>
            </div>
            {themeMode === option.mode && (
              <div className="w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-white"></div>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// Quick theme toggle button component
export const ThemeToggle = ({ className = "" }) => {
  const { themeMode, actualTheme, updateThemeMode, availableModes } = useTheme();

  const handleToggle = () => {
    if (themeMode === availableModes.SYSTEM) {
      // If system mode, switch to the opposite of current actual theme
      updateThemeMode(actualTheme === 'dark' ? availableModes.LIGHT : availableModes.DARK);
    } else if (themeMode === availableModes.LIGHT) {
      updateThemeMode(availableModes.DARK);
    } else {
      updateThemeMode(availableModes.LIGHT);
    }
  };

  const getIcon = () => {
    if (themeMode === availableModes.SYSTEM) {
      return '💻';
    }
    return actualTheme === 'dark' ? '🌙' : '☀️';
  };

  const getLabel = () => {
    if (themeMode === availableModes.SYSTEM) {
      return `System (${actualTheme})`;
    }
    return actualTheme === 'dark' ? 'Dark' : 'Light';
  };

  return (
    <button
      onClick={handleToggle}
      className={`theme-toggle flex items-center space-x-2 p-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 ${className}`}
      title={`Current theme: ${getLabel()}. Click to switch.`}
    >
      <span className="text-lg" role="img" aria-label="Theme">
        {getIcon()}
      </span>
      <span className="text-sm font-medium hidden sm:inline">
        {getLabel()}
      </span>
    </button>
  );
};

export default ThemeContext;